import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, LogOut } from 'lucide-react';
import { api } from '@/api/client';
import { Button, buttonClassName } from '@/components/ui/Button';
import { Card, Overline } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { Skeleton } from '@/components/ui/States';
import { Pill } from '@/components/ui/Badge';
import { roleLabel } from '@/features/admin-content/staff';
import { AccountSettings } from '@/features/account/AccountSettings';
import { useIsStaff } from '@/features/admin-content/use-current-user';
import { summariseAccess } from '@/features/billing/access-label';
import { useAccess } from '@/features/billing/use-access';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/account/')({
  component: AccountPage,
});

function AccountPage() {
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/api/v1/me'),
    enabled: Boolean(session),
  });

  const access = useAccess(Boolean(session));
  const isStaff = useIsStaff();

  if (isPending) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!session) {
    return (
      <Card padding="lg" className="grid max-w-auth gap-4">
        <h1 className="font-display text-h3">Log in to see your account</h1>
        <Link to="/sign-in" className={buttonClassName()}>
          Log in
        </Link>
      </Card>
    );
  }

  return (
    <div className="grid max-w-prose grid-cols-[minmax(0,1fr)] gap-6">
      <PageHeader title="Account" description="Your details, goals, plan and sign-out." />

      <Card padding="lg" className="grid gap-4">
        <Overline>Your details</Overline>
        <dl className="grid gap-3 text-body">
          <Row label="Name" value={me.data?.name} />
          <Row label="Email" value={me.data?.email ?? session.user.email} />
          {me.data?.roles && me.data.roles.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3 last:border-0 last:pb-0">
              <dt className="text-muted">Roles</dt>
              <dd className="flex flex-wrap justify-end gap-1.5">
                {me.data.roles.map((slug) => (
                  <Pill key={slug} tone="neutral">
                    {roleLabel(slug)}
                  </Pill>
                ))}
              </dd>
            </div>
          ) : null}
        </dl>
      </Card>

      <AccountSettings isStaff={isStaff} />

      <Card padding="lg" className="grid gap-4">
        <Overline>Your plan</Overline>
        {access.isPending || !access.data ? (
          <Skeleton className="h-6 w-40" />
        ) : (
          <div className="grid gap-1">
            <span className="font-display text-h4">{summariseAccess(access.data).title}</span>
            <span className="text-small text-muted">{summariseAccess(access.data).detail}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2.5">
          <Link to="/account/billing" className={buttonClassName({ variant: 'secondary' })}>
            <CreditCard className="mr-2 size-5" aria-hidden="true" />
            Manage billing
          </Link>
          <Link to="/pricing" className={buttonClassName({ variant: 'ghost' })}>
            See all plans
          </Link>
        </div>
      </Card>

      <Card padding="lg" className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <b className="text-body">Log out of this device</b>
          <p className="text-small text-muted">Your answers are saved.</p>
        </div>
        <Button
          variant="secondary"
          icon={LogOut}
          onClick={() => {
            void authClient.signOut().then(() => navigate({ to: '/' }));
          }}
        >
          Log out
        </Button>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | undefined }) {
  return (
    <div className="flex flex-wrap justify-between gap-2 border-b border-border pb-3 last:border-0 last:pb-0">
      <dt className="text-muted">{label}</dt>
      <dd className="font-bold">{value ?? <Skeleton className="h-4 w-32" />}</dd>
    </div>
  );
}
